import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IRootState, AppDispatch } from '@/store';
import { logout, type AuthUser } from '@/store/authSlice';
import { usePermission } from '@/hooks/usePermission';
import Dropdown from '../Dropdown';
import ConnectionBadge from '../Realtime/ConnectionBadge';
import NotificationsBell from '../Notifications/NotificationsBell';
import ThemeToggle from '../ThemeToggle';

const getInitials = (name?: string | null) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Header = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const themeConfig = useSelector((state: IRootState) => state.themeConfig);
    const authUser = useSelector((state: IRootState) => state.auth.user);
    const authRoles = useSelector((state: IRootState) => state.auth.roles);
    const dispatch = useDispatch<AppDispatch>();
    const { hasAnyPermission, isSuperAdmin } = usePermission();
    const canAdminister = hasAnyPermission(['users.manage', 'roles.manage', 'sectors.manage', 'audit.view']);
    const primaryRole = authRoles[0];
    const fullName = authUser?.name || 'Usuário';

    const handleLogout = async (e: React.MouseEvent) => {
        e.preventDefault();
        await dispatch(logout());
        navigate('/login', { replace: true });
    };

    useEffect(() => {
        const selector = document.querySelector('ul.horizontal-menu a[href="' + window.location.pathname + '"]');
        if (selector) {
            selector.classList.add('active');
        }
    }, [location]);

    return (
        <header className={`z-40 print:hidden ${themeConfig.semidark && themeConfig.menu === 'horizontal' ? 'dark' : ''}`}>
            {/* App instalado (viewport-fit=cover): o header pinta a área da barra de status. */}
            <div className="shadow-sm bg-white dark:bg-surface-card pt-[env(safe-area-inset-top)] [@media(display-mode:standalone)]:pt-[calc(env(safe-area-inset-top)+1rem)]">
                {/* ── Desktop: uma linha ── */}
                <div className="header-gradient-border relative bg-white dark:bg-surface-card hidden sm:flex w-full items-center px-5 py-1.5">
                    <div className="flex items-center ltr:mr-2 rtl:ml-2">
                        <Link to="/" className="main-logo flex items-center shrink-0">
                            <img className="h-9 ltr:-ml-1 rtl:-mr-1 inline dark:hidden" src="/assets/images/logo-color.webp" alt="Capital Cloud" />
                            <img className="h-9 ltr:-ml-1 rtl:-mr-1 hidden dark:inline" src="/assets/images/logo-white.webp" alt="Capital Cloud" />
                        </Link>
                    </div>

                    <div className="flex-1 flex items-center justify-end space-x-1.5 lg:space-x-2 rtl:space-x-reverse dark:text-[#d0d2d6]">
                        <NotificationsBell />
                        <ThemeToggle />
                        <UserDropdown authUser={authUser} fullName={fullName} primaryRole={primaryRole} canAdminister={canAdminister} isSuperAdmin={isSuperAdmin} handleLogout={handleLogout} />
                    </div>
                </div>

                {/* ── Mobile: full-width, logo + ícones ── */}
                <div className="sm:hidden header-gradient-border relative bg-white dark:bg-surface-card flex items-center justify-between px-3 py-3">
                    <Link to="/" className="main-logo flex items-center shrink-0">
                        <img className="h-9 inline dark:hidden" src="/assets/images/logo-color.webp" alt="Capital Cloud" />
                        <img className="h-9 hidden dark:inline" src="/assets/images/logo-white.webp" alt="Capital Cloud" />
                    </Link>
                    <div className="flex items-center gap-1.5 dark:text-[#d0d2d6]">
                        <NotificationsBell size="sm" />
                        <ThemeToggle size="sm" />
                    </div>
                </div>
            </div>
        </header>
    );
};

/* ── User dropdown ── */
const UserDropdown = ({
    authUser,
    fullName,
    primaryRole,
    canAdminister,
    isSuperAdmin,
    handleLogout,
    size,
}: {
    authUser: AuthUser | null;
    fullName: string;
    primaryRole?: string;
    canAdminister: boolean;
    isSuperAdmin: boolean;
    handleLogout: (e: React.MouseEvent) => void;
    size?: 'sm';
}) => {
    const avatarClass =
        size === 'sm'
            ? 'w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-semibold'
            : 'w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold';
    const btnClass =
        size === 'sm'
            ? 'relative group flex items-center p-0.5 rounded-full hover:bg-white-light/60 dark:hover:bg-surface-hover transition-colors'
            : 'relative group flex items-center px-1 py-1 rounded-full hover:bg-white-light/60 dark:hover:bg-surface-hover transition-colors';

    return (
        <div className="dropdown shrink-0 flex">
            <Dropdown offset={[0, 8]} placement="bottom-end" btnClassName={btnClass} button={<div className={avatarClass}>{getInitials(authUser?.name)}</div>}>
                <ul className="text-dark dark:text-white-dark !py-0 w-[260px] font-semibold dark:text-white-light/90">
                    <li>
                        <div className="px-4 py-4 text-center">
                            <h4 className="text-sm font-semibold truncate">{fullName}</h4>
                            <span className="block text-xs text-black/60 dark:text-white-dark/60 truncate">{authUser?.email || ''}</span>
                            {primaryRole && <span className="mt-2 inline-block text-[10px] font-semibold px-2 py-0.5 rounded bg-primary/15 text-primary uppercase tracking-wide">{primaryRole}</span>}
                        </div>
                    </li>
                    <li className="border-t border-white-light dark:border-surface-border">
                        <Link to="/me" className="!py-3 hover:text-primary !justify-center">
                            <svg className="ltr:mr-2 rtl:ml-2 shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle opacity="0.5" cx="12" cy="6" r="4" fill="currentColor" />
                                <path d="M20 17.5C20 19.9853 20 22 12 22C4 22 4 19.9853 4 17.5C4 15.0147 7.58172 13 12 13C16.4183 13 20 15.0147 20 17.5Z" fill="currentColor" />
                            </svg>
                            Meu Perfil
                        </Link>
                    </li>
                    <li className="border-t border-white-light dark:border-surface-border">
                        <Link to="/apps" className="!py-3 hover:text-primary !justify-center">
                            <svg className="ltr:mr-2 rtl:ml-2 shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle opacity="0.5" cx="12" cy="6" r="4" fill="currentColor" />
                                <path d="M20 17.5C20 19.9853 20 22 12 22C4 22 4 19.9853 4 17.5C4 15.0147 7.58172 13 12 13C16.4183 13 20 15.0147 20 17.5Z" fill="currentColor" />
                            </svg>
                            Hub de aplicações
                        </Link>
                    </li>
                    {canAdminister && (
                        <li className="border-t border-white-light dark:border-surface-border">
                            <Link to="/admin" className="!py-3 hover:text-primary !justify-center">
                                <svg className="ltr:mr-2 rtl:ml-2 shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        opacity="0.5"
                                        d="M20.7906 8.121L20.1006 6.931C19.7306 6.291 19.5406 5.971 19.2206 5.861C18.9006 5.751 18.5306 5.891 17.7906 6.171L16.2406 6.761C15.6306 6.531 14.9906 6.361 14.3306 6.261L13.6306 4.8C13.2906 4.1 13.1206 3.751 12.8206 3.551C12.5206 3.351 12.1406 3.351 11.3806 3.351H10.6206C9.86056 3.351 9.48056 3.351 9.18056 3.551C8.88056 3.751 8.71056 4.1 8.37056 4.8L7.67056 6.261C7.01056 6.361 6.37056 6.531 5.76056 6.761L4.21056 6.171C3.47056 5.891 3.10056 5.751 2.78056 5.861C2.46056 5.971 2.27056 6.291 1.90056 6.931L1.21056 8.121C0.83056 8.771 0.64056 9.101 0.69056 9.421C0.74056 9.741 1.01056 10.011 1.55056 10.551L2.58056 11.551C2.53056 11.901 2.50056 12.2511 2.50056 12.601C2.50056 12.9511 2.53056 13.3011 2.58056 13.6511L1.55056 14.651C1.01056 15.191 0.74056 15.461 0.69056 15.781C0.64056 16.101 0.83056 16.431 1.21056 17.081L1.90056 18.271C2.27056 18.911 2.46056 19.231 2.78056 19.341C3.10056 19.451 3.47056 19.311 4.21056 19.031L5.76056 18.441C6.37056 18.671 7.01056 18.841 7.67056 18.941L8.37056 20.401C8.71056 21.101 8.88056 21.451 9.18056 21.651C9.48056 21.851 9.86056 21.851 10.6206 21.851H11.3806C12.1406 21.851 12.5206 21.851 12.8206 21.651C13.1206 21.451 13.2906 21.101 13.6306 20.401L14.3306 18.941C14.9906 18.841 15.6306 18.671 16.2406 18.441L17.7906 19.031C18.5306 19.311 18.9006 19.451 19.2206 19.341C19.5406 19.231 19.7306 18.911 20.1006 18.271L20.7906 17.081C21.1706 16.431 21.3606 16.101 21.3106 15.781C21.2606 15.461 20.9906 15.191 20.4506 14.651L19.4206 13.6511C19.4706 13.3011 19.5006 12.9511 19.5006 12.601C19.5006 12.2511 19.4706 11.901 19.4206 11.551L20.4506 10.551C20.9906 10.011 21.2606 9.741 21.3106 9.421C21.3606 9.101 21.1706 8.771 20.7906 8.121Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M11.0005 15.5C12.9335 15.5 14.5005 13.933 14.5005 12C14.5005 10.067 12.9335 8.5 11.0005 8.5C9.06748 8.5 7.50048 10.067 7.50048 12C7.50048 13.933 9.06748 15.5 11.0005 15.5Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                Painel Administrativo
                            </Link>
                        </li>
                    )}
                    <li className="border-t border-white-light dark:border-surface-border">
                        {isSuperAdmin ? (
                            <div className="grid grid-cols-2 divide-x divide-white-light dark:divide-surface-border">
                                <div className="flex items-center justify-center py-3 gap-2 text-xs">
                                    <ConnectionBadge />
                                    <span className="text-white-dark/70">Tempo real</span>
                                </div>
                                <Link to="/login" onClick={handleLogout} className="text-danger !py-3 !justify-center flex items-center">
                                    <svg className="ltr:mr-2 rtl:ml-2 rotate-90 shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            opacity="0.5"
                                            d="M17 9.00195C19.175 9.01406 20.3529 9.11051 21.1213 9.8789C22 10.7576 22 12.1718 22 15.0002V16.0002C22 18.8286 22 20.2429 21.1213 21.1215C20.2426 22.0002 18.8284 22.0002 16 22.0002H8C5.17157 22.0002 3.75736 22.0002 2.87868 21.1215C2 20.2429 2 18.8286 2 16.0002L2 15.0002C2 12.1718 2 10.7576 2.87868 9.87889C3.64706 9.11051 4.82497 9.01406 7 9.00195"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                        <path d="M12 15L12 2M12 2L15 5.5M12 2L9 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Sair
                                </Link>
                            </div>
                        ) : (
                            <Link to="/login" onClick={handleLogout} className="text-danger !py-3 !justify-center">
                                <svg className="ltr:mr-2 rtl:ml-2 rotate-90 shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        opacity="0.5"
                                        d="M17 9.00195C19.175 9.01406 20.3529 9.11051 21.1213 9.8789C22 10.7576 22 12.1718 22 15.0002V16.0002C22 18.8286 22 20.2429 21.1213 21.1215C20.2426 22.0002 18.8284 22.0002 16 22.0002H8C5.17157 22.0002 3.75736 22.0002 2.87868 21.1215C2 20.2429 2 18.8286 2 16.0002L2 15.0002C2 12.1718 2 10.7576 2.87868 9.87889C3.64706 9.11051 4.82497 9.01406 7 9.00195"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                    <path d="M12 15L12 2M12 2L15 5.5M12 2L9 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Sair
                            </Link>
                        )}
                    </li>
                </ul>
            </Dropdown>
        </div>
    );
};

export default Header;
